import { defineComponent } from '~/scripts/utils/alpine'

/**
 * BlogPosts component
 * Handles filter changes and "Load more" pagination via AJAX.
 *
 * Expected markup (simplified):
 * <div x-data="BlogPosts" data-api-base="/wp-json/..." data-lang="fr" data-cat="" x-ref="wrapper">
 *   <!-- Filter button -->
 *   <span x-ref="filterLabel"></span>
 *   <!-- Posts grid coming from section-posts.php -->
 *   <div x-ref="grid"></div>
 *   <div x-ref="loadMoreContainer"></div>
 * </div>
 */
export default defineComponent(() => ({
  // Props passed via data attributes
  lang: 'fr',
  cat: undefined as number | undefined,
  apiBase: '',

  // Internal state
  page: 1,
  selectedLabel: '',
  loading: false,
  loadingMore: false,

  init() {
    // Read dataset from root element
    this.lang = this.$root.dataset.lang || 'fr'
    this.apiBase = this.$root.dataset.apiBase || ''
    this.cat = this.$root.dataset.cat
      ? Number(this.$root.dataset.cat)
      : undefined

    // Store initial label
    const labelElement = this.$refs.filterLabel as HTMLElement | undefined
    this.selectedLabel = labelElement?.textContent?.trim() || ''
  },

  /**
   * Change treatment filter (called on option click)
   */
  async changeFilter(target: HTMLElement) {
    this.loading = true
    // Determine ID and label based on element type
    let idString = ''
    let labelText = ''
    if (target instanceof HTMLSelectElement) {
      idString = target.value
      const opt = (target as HTMLSelectElement).options[target.selectedIndex]
      labelText = opt?.text || ''
    } else {
      idString = target.dataset.treatmentId || ''
      labelText = target.textContent?.trim() || ''
    }
    this.cat = idString ? Number(idString) : undefined
    this.selectedLabel = labelText
    this.page = 1

    // Update the URL with the selected category (SEO, shareable, navigation)
    const url = new URL(globalThis.location.href)
    if (idString) {
      url.searchParams.set('cat', idString)
    } else {
      url.searchParams.delete('cat')
    }
    globalThis.history.pushState({}, '', url.toString())

    // Update button label visually (SR friendly)
    if (this.$refs.filterLabel) {
      ;(this.$refs.filterLabel as HTMLElement).textContent = this.selectedLabel
    }

    // Close dropdown if any (the filter wrapper has x-data="{open:false}")
    const dropdownWrapper = target.closest('[x-data]') as
      | HTMLElement
      | undefined
    if (dropdownWrapper) {
      // Try to close the Alpine dropdown programmatically
      // Alpine stores the component instance in __x
      const alpineData =
        (
          dropdownWrapper as unknown as {
            _x_dataStack?: Record<string, unknown>[]
          }
        )._x_dataStack?.[0] ??
        (
          dropdownWrapper as unknown as {
            __x?: { $data?: Record<string, unknown> }
          }
        ).__x?.$data

      if (alpineData && 'open' in alpineData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(alpineData as any).open = false
      }
    }

    await this.fetchPosts()
    this.loading = false
  },

  /**
   * Load more items using the button in the list
   */
  async loadMore(button: HTMLButtonElement) {
    this.loading = true
    this.loadingMore = true
    let url = button.dataset.url
    if (!url) return

    // Ensure the cat parameter is correctly set
    if (!url.includes('cat=') && this.cat !== undefined) {
      url += `&cat=${this.cat}`
    } else if (!url.includes('cat=')) {
      url += '&cat=0'
    }

    button.disabled = true
    try {
      // Add spinner to arrow icon inside button
      const icon = button.querySelector('iconify-icon') as HTMLElement | null
      if (icon) {
        icon.classList.remove('group-hover:animate-bounce')
        icon.classList.add('animate-spin')
      }
      const response = await fetch(url)
      if (!response.ok) throw new Error(response.statusText)
      const json = await response.json()
      const wrapper = document.createElement('div')
      wrapper.innerHTML = json.data

      // Append new cards
      const newGrid = wrapper.querySelector('div.grid') as HTMLDivElement | null
      if (newGrid && this.$refs.grid) {
        this.$refs.grid.insertAdjacentHTML('beforeend', newGrid.innerHTML)
      }

      // Replace or remove the button
      const nextButton = wrapper.querySelector(
        'button[data-url]',
      ) as HTMLButtonElement | null
      if (nextButton) {
        button.replaceWith(nextButton)
      } else {
        button.remove()
      }
      this.page += 1
      this.loading = false
      this.loadingMore = false
    } catch (error) {
      console.error(error)
      button.disabled = false
      this.loading = false
      this.loadingMore = false
    }
  },

  /**
   * Fetch posts (page reset)
   */
  async fetchPosts() {
    this.loading = true
    this.loadingMore = false
    const params = new URLSearchParams()
    params.set('page', this.page.toString())
    params.set('lang', this.lang)
    if (this.cat === undefined) {
      params.set('cat', '0')
    } else {
      params.set('cat', this.cat.toString())
    }
    const url = `${this.apiBase}?${params.toString()}`

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(response.statusText)
      const json = await response.json()

      const wrapper = document.createElement('div')
      wrapper.innerHTML = json.data

      // Replace grid content
      const newGrid = wrapper.querySelector('div.grid') as HTMLDivElement | null
      if (newGrid && this.$refs.grid) {
        ;(this.$refs.grid as HTMLElement).innerHTML = newGrid.innerHTML
      }

      // Update / replace load more button container
      const newLoadMore = wrapper.querySelector(
        'button[data-url]',
      ) as HTMLButtonElement | null
      const loadMoreContainer = this.$refs.loadMoreContainer as HTMLElement
      if (loadMoreContainer) {
        loadMoreContainer.innerHTML = ''
        if (newLoadMore) {
          loadMoreContainer.append(newLoadMore)
        }
      }
      this.loading = false
      this.loadingMore = false
    } catch (error) {
      console.error(error)
      this.loading = false
      this.loadingMore = false
    }
  },
}))
